import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { hubClient } from '../../services/hubClient';
import { errorText } from '../../lib/assistantView';
import { isBotSystem } from '../../lib/botNav';
import type { BlockLinkRow, LinkThresholds } from '../../lib/blockLinksView';
import { appendPage, whyRow, type WhyRow, type WhyTrace } from '../../lib/blockWhy';

const PAGE = 50;

const TONE: Record<WhyRow['tone'], string> = {
  sent: 'text-green-700',
  close: 'text-amber-700',
  none: 'text-gray-500',
  test: 'text-blue-700',
};

/**
 * Knowledge › Blocks: the bot's latest real customer questions and why each one got (or did not get) a block — read
 * from the answer traces tool-service writes (bot-scoped, 90-day purge). "Test this" copies the question into the
 * Test box so the operator can edit a link and try again.
 */
export function RecentBlockQuestions({
  system, botId, channel, links, thresholds, onTest, onAddExample,
}: {
  system: string;
  botId: string;
  /** v2: the block channel picked on the Blocks screen; questions from other channels are left out. */
  channel?: string;
  links: BlockLinkRow[];
  thresholds: LinkThresholds;
  onTest: (question: string) => void;
  onAddExample: (linkId: string, text: string) => void;
}) {
  const [traces, setTraces] = useState<WhyTrace[] | null>(null);
  const [total, setTotal] = useState(0);
  const [more, setMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [notSentOnly, setNotSentOnly] = useState(false);
  // Bumped on every fresh load: an answer for an earlier channel or bot arriving late is dropped.
  const view = useRef(0);

  const load = useCallback(async () => {
    if (!isBotSystem(system)) return;
    const mine = ++view.current;
    setLoading(true);
    setError(null);
    try {
      const page = await hubClient.recentBlockTraces({ system, botId }, PAGE, 0, channel);
      if (mine !== view.current) return;
      setTraces(page.items);
      setTotal(page.total);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, [system, botId, channel]);

  useEffect(() => {
    void load();
  }, [load]);

  const showMore = async () => {
    if (!traces || !isBotSystem(system)) return;
    const mine = view.current;
    setMore(true);
    setError(null);
    try {
      const page = await hubClient.recentBlockTraces({ system, botId }, PAGE, traces.length, channel);
      if (mine !== view.current) return;
      setTraces((shown) => appendPage(shown ?? [], page.items));
      setTotal(page.total);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setMore(false);
    }
  };

  // Titles are applied on render, so the link list reloading never refetches the questions.
  const rows = useMemo(() => {
    if (!traces) return null;
    const titles = Object.fromEntries(links.map((l) => [l.id, l.title]));
    return traces
      .map((t) => whyRow(t, titles))
      .filter((x): x is WhyRow => x !== null && (!notSentOnly || x.tone === 'close' || x.tone === 'none'));
  }, [traces, links, notSentOnly]);
  const linkIds = useMemo(() => new Set(links.map((l) => l.id)), [links]);

  if (!isBotSystem(system)) return null;
  return (
    <div className="rounded border border-gray-200 bg-white p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-gray-900">Recent customer questions</div>
        <button type="button" disabled={loading} className="text-sm text-blue-700 disabled:opacity-50" onClick={() => void load()}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      <p className="text-xs text-gray-600">
        The latest questions{channel ? ' on this channel' : ''} answered while block sending was on, and why a block was or was not sent. A block is sent
        alone at {thresholds.blockOnly.toFixed(2)} or more, with the AI answer at {thresholds.attach.toFixed(2)} or more
        (platform admins change them in Embedding profiles).
      </p>
      <label className="flex items-center gap-2 text-xs text-gray-700">
        <input type="checkbox" checked={notSentOnly} onChange={(e) => setNotSentOnly(e.target.checked)} /> Not sent only
      </label>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {rows && rows.length === 0 && (
        <p className="text-sm text-gray-500">{notSentOnly ? 'Every recent question got a block.' : 'No customer questions with block sending yet.'}</p>
      )}
      {rows && rows.length > 0 && (
        <ul className="divide-y divide-gray-100 text-sm">
          {rows.map((r) => (
            <li key={r.id} className="py-2 space-y-0.5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <span className="text-gray-900">{r.question}</span>
                  {r.matchedAs && <span className="ml-2 text-xs text-gray-500">matched as <span className="font-mono">{r.matchedAs}</span></span>}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-xs text-gray-400">{new Date(r.createdAt).toLocaleString()}</span>
                  {r.weak && r.linkId && linkIds.has(r.linkId) && (
                    <button type="button" className="text-xs text-blue-700" onClick={() => onAddExample(r.linkId!, r.exampleText)}>
                      Add as example
                    </button>
                  )}
                  <button type="button" className="text-xs text-blue-700" onClick={() => onTest(r.question)}>
                    Test this
                  </button>
                </div>
              </div>
              <div className={TONE[r.tone]}>{r.headline}</div>
              {r.detail && <div className="text-xs text-gray-600">{r.detail}</div>}
            </li>
          ))}
        </ul>
      )}
      {traces && traces.length < total && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Showing {traces.length} of {total}</span>
          <button type="button" disabled={more} className="text-sm text-blue-700 disabled:opacity-50" onClick={() => void showMore()}>
            {more ? 'Loading…' : 'Show more'}
          </button>
        </div>
      )}
    </div>
  );
}
